import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, CalendarClock, MessageSquare, Mail, Flame, Search, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';
import { CRMOpportunityCard } from './CRMOpportunityCard';
import { runOpportunityAction } from './crmOpportunityActions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase 6D — Daily Revenue Worklist. Pure read/prioritization layer on top
// of the existing /api/crm/opportunities data — no new datastore, no
// automated sending. Sections ordered by urgency/revenue proximity.
const SECTIONS = [
  { key: 'followup_overdue', label: 'Follow-Up Overdue', icon: CalendarClock, accent: 'text-red-600' },
  { key: 'followup_due_today', label: 'Follow-Up Due Today', icon: CalendarClock, accent: 'text-amber-600' },
  { key: 'response_needed', label: 'Responses Needing Action', icon: MessageSquare, accent: 'text-blue-600' },
  { key: 'ready_for_outreach', label: 'Ready for Outreach', icon: Mail, accent: 'text-emerald-600' },
  { key: 'awaiting_response', label: 'Awaiting Response', icon: Flame, accent: 'text-gray-500' },
  { key: 'discovery_needed', label: 'Discovery Needed', icon: Search, accent: 'text-indigo-600' },
  { key: 'agreement_opportunity', label: 'Agreement Opportunity', icon: FileText, accent: 'text-amber-700' },
];

const CRMTodayWorklist = ({ onOpenCompany, onOutreach, refreshKey, bumpRefresh }) => {
  const [data, setData] = useState({ opportunities: [], bucket_counts: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    // 2000 matches the backend's own internal eligibility-scan cap
    // (_compute_opportunity_rows), so every bucketed row is guaranteed to
    // be included here regardless of dataset size — a lower limit here
    // combined with priority_score sorting was silently dropping genuine
    // low-score-but-due-today/awaiting-response rows off the end.
    fetchWithAuth(`${API_URL}/api/crm/opportunities?limit=2000`)
      .then(setData)
      .catch(() => toast.error("Could not load today's worklist"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const runAction = async (opp, actionKey, extra) => {
    try {
      await runOpportunityAction(API_URL, fetchWithAuth, opp, actionKey, extra);
      toast.success('Updated');
      bumpRefresh();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="today-worklist-loading"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  const byBucket = {};
  data.opportunities.forEach(o => { (byBucket[o.bucket] = byBucket[o.bucket] || []).push(o); });

  const c = data.bucket_counts || {};
  const followUpsDue = (c.followup_overdue || 0) + (c.followup_due_today || 0);
  const anyItems = SECTIONS.some(s => (byBucket[s.key] || []).length > 0);

  return (
    <div className="space-y-6" data-testid="today-worklist">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="today-summary-cards">
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="summary-ready-for-outreach">
          <div className="text-2xl font-bold text-emerald-600">{c.ready_for_outreach || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Outreach Ready</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="summary-followups-due">
          <div className="text-2xl font-bold text-amber-600">{followUpsDue}</div>
          <div className="text-xs text-gray-500 mt-1">Follow-Ups Due ({c.followup_overdue || 0} overdue)</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="summary-responses-needing-action">
          <div className="text-2xl font-bold text-blue-600">{c.response_needed || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Responses Needing Action</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="summary-awaiting-response">
          <div className="text-2xl font-bold text-gray-500">{c.awaiting_response || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Awaiting Response</div>
        </div>
      </div>

      {!anyItems ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="today-empty-state">Nothing needs attention right now — nice work.</div>
      ) : SECTIONS.map(section => {
        const items = byBucket[section.key] || [];
        if (!items.length) return null;
        const Icon = section.icon;
        return (
          <div key={section.key} data-testid={`today-section-${section.key}`}>
            <h3 className={`text-sm font-semibold flex items-center gap-1.5 mb-2 ${section.accent}`}>
              <Icon className="h-4 w-4" /> {section.label} ({items.length})
            </h3>
            <div className="space-y-3">
              {items.map(opp => (
                <CRMOpportunityCard key={opp.id} opp={opp} onOpenCompany={onOpenCompany} onOutreach={onOutreach} onRunAction={runAction} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CRMTodayWorklist;
